/**
 * Access logs - consumer API traffic from service accounts into Axis.
 *
 * Wires `GET /api/v1/admin/access-logs` (serviceAccountId, entity, from/to,
 * limit/offset). Distinct from the admin audit log.
 *
 * Columns: Time · Service · Data type · Request · Status · Duration · Records.
 */
import { el, mount } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import {
  renderLoadingState,
  renderErrorState,
  renderEmptyState,
  renderUnavailableState,
} from "../components/states";
import { renderStatusText } from "../components/statusText";
import { renderAbsoluteTime } from "../components/dateTime";
import { EMPTY, ENTITY_ORDER, entityLabel, formatNumber } from "../utils/format";
import { openModal } from "../components/modal";
import { listAccessLogs } from "../services/accessLogs";
import { listServices } from "../services/admin";
import { ApiError } from "../services/apiClient";
import type { AccessLogItem, ServiceAccount } from "../types/api";

const LIMIT_OPTIONS = [25, 50, 100, 200];

interface Filters {
  serviceAccountId: string;
  entity: string;
  from: string;
  to: string;
}

export class AccessLogsPage {
  private body: HTMLElement = el("div", {}, [renderLoadingState("Loading API access logs…")]);
  private paginationEl: HTMLElement = el("div", {});
  private serviceSelect!: HTMLSelectElement;
  private entitySelect!: HTMLSelectElement;
  private fromInput!: HTMLInputElement;
  private toInput!: HTMLInputElement;
  private filters: Filters = { serviceAccountId: "", entity: "", from: "", to: "" };
  private services: ServiceAccount[] = [];
  private limit = 50;
  private offset = 0;
  private total = 0;

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    this.serviceSelect = headerSelect(
      "Filter by service account",
      [el("option", { value: "" }, ["Service"])],
      (value) => {
        this.filters.serviceAccountId = value;
        this.offset = 0;
        void this.load();
      }
    );

    this.entitySelect = headerSelect(
      "Filter by data type",
      [
        el("option", { value: "" }, ["Data type"]),
        ...ENTITY_ORDER.map((e) => el("option", { value: e }, [entityLabel(e)])),
        el("option", { value: "unknown" }, ["Unknown"]),
      ],
      (value) => {
        this.filters.entity = value;
        this.offset = 0;
        void this.load();
      }
    );

    this.fromInput = el("input", {
      className: "input input--sm input--date",
      type: "date",
      title: "From (inclusive)",
      "aria-label": "From date",
      onchange: (ev: Event) => {
        this.filters.from = toIsoStart((ev.target as HTMLInputElement).value);
        this.offset = 0;
        void this.load();
      },
    }) as HTMLInputElement;

    this.toInput = el("input", {
      className: "input input--sm input--date",
      type: "date",
      title: "To (inclusive)",
      "aria-label": "To date",
      onchange: (ev: Event) => {
        this.filters.to = toIsoEnd((ev.target as HTMLInputElement).value);
        this.offset = 0;
        void this.load();
      },
    }) as HTMLInputElement;

    const clearButton = el(
      "button",
      {
        className: "btn-link",
        type: "button",
        onclick: () => this.clearFilters(),
      },
      ["Clear filters"]
    );

    const rangeToolbar = el("div", { className: "toolbar toolbar--range" }, [
      el("div", { className: "range-filter" }, [
        el("span", { className: "range-filter__label" }, ["From"]),
        this.fromInput,
        el("span", { className: "range-filter__sep" }, ["to"]),
        this.toInput,
      ]),
      clearButton,
    ]);

    const refreshButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        "aria-label": "Refresh API access logs",
        onclick: () => void this.load(),
      },
      ["Refresh"]
    );

    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "API Access Logs",
          subtitle: "API calls made by service accounts.",
          back: { fallbackPath: "/dashboard", navigate, preferHistory: false, label: "Back to Dashboard" },
          actions: [refreshButton],
        }),
        el("div", { className: "panel panel--standard" }, [rangeToolbar, this.body, this.paginationEl]),
      ])
    );

    void this.bootstrap();
  }

  destroy(): void {
    // No timers.
  }

  private async bootstrap(): Promise<void> {
    await this.load();
  }

  private syncServiceOptions(): void {
    const current = this.serviceSelect.value;
    this.serviceSelect.replaceChildren(
      el("option", { value: "" }, ["Service"]),
      ...this.services
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((s) => el("option", { value: String(s.id) }, [s.name]))
    );
    this.serviceSelect.value = current;
    syncHeaderActive(this.serviceSelect, current !== "");
  }

  private clearFilters(): void {
    this.filters = { serviceAccountId: "", entity: "", from: "", to: "" };
    this.serviceSelect.value = "";
    this.entitySelect.value = "";
    this.fromInput.value = "";
    this.toInput.value = "";
    syncHeaderActive(this.serviceSelect, false);
    syncHeaderActive(this.entitySelect, false);
    this.offset = 0;
    void this.load();
  }

  private async load(): Promise<void> {
    mount(this.body, renderLoadingState("Loading API access logs…"));
    mount(this.paginationEl, null);

    try {
      try {
        this.services = await listServices();
        this.syncServiceOptions();
      } catch {
        // Keep prior service options if the accounts list fails.
      }

      const serviceAccountId = this.filters.serviceAccountId
        ? Number(this.filters.serviceAccountId)
        : undefined;

      const data = await listAccessLogs({
        serviceAccountId: Number.isFinite(serviceAccountId) ? serviceAccountId : undefined,
        entity: this.filters.entity || undefined,
        from: this.filters.from || undefined,
        to: this.filters.to || undefined,
        limit: this.limit,
        offset: this.offset,
      });

      this.total = data.pagination.total;

      if (!data.items.length) {
        mount(
          this.body,
          el("div", {}, [
            this.renderTable([]),
            renderEmptyState(
              this.offset > 0 ? "No more access log entries." : "No entries match these filters."
            ),
          ])
        );
      } else {
        mount(this.body, this.renderTable(data.items));
      }

      mount(this.paginationEl, this.renderPagination(data.items.length));
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 503)) {
        mount(
          this.body,
          renderUnavailableState(err.message, "API access logs could not be loaded.")
        );
      } else {
        mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.load() }));
      }
    }
  }

  private renderTable(items: AccessLogItem[]): HTMLElement {
    const table = el("table", { className: "data-table" }, [
      el("thead", {}, [
        el("tr", {}, [
          el("th", { scope: "col" }, ["Time"]),
          filterHeader(this.serviceSelect),
          filterHeader(this.entitySelect),
          el("th", { scope: "col" }, ["Request"]),
          el("th", { scope: "col" }, ["Status"]),
          el("th", { scope: "col" }, ["Duration"]),
          el("th", { scope: "col", className: "numeric" }, ["Records"]),
        ]),
      ]),
      el(
        "tbody",
        {},
        items.map((item) => {
          const failed = item.statusCode >= 400;
          return el(
            "tr",
            {
              className: ["data-table__row--nav", failed ? "flag-danger" : ""].filter(Boolean).join(" "),
              tabIndex: 0,
              role: "button",
              "aria-label": `Access log ${item.id}: ${item.method} ${item.endpoint}`,
              onclick: () => this.openDetail(item),
              onkeydown: (ev: KeyboardEvent) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  this.openDetail(item);
                }
              },
            },
            [
              el("td", {}, [renderAbsoluteTime(item.requestedAt)]),
              el("td", {}, [item.serviceAccountName || EMPTY]),
              el("td", {}, [entityLabel(item.entity)]),
              el("td", {}, [
                el("div", {}, [
                  el("span", { className: "mono" }, [`${item.method} `]),
                  el("span", { className: "mono data-table__cell-wrap" }, [item.endpoint]),
                ]),
              ]),
              el("td", {}, [statusLabel(item.statusCode)]),
              el("td", {}, [formatDurationMs(item.durationMs)]),
              el("td", { className: "numeric tnum" }, [formatNumber(item.recordCount)]),
            ]
          );
        })
      ),
    ]);

    return el("div", { className: "data-table-wrap data-table-wrap--fit" }, [table]);
  }

  private renderPagination(pageCount: number): HTMLElement {
    const start = this.total === 0 ? 0 : this.offset + 1;
    const end = this.offset + pageCount;

    const limitSelect = el(
      "select",
      {
        className: "input input--sm",
        "aria-label": "Rows per page",
        onchange: (ev: Event) => {
          this.limit = Number((ev.target as HTMLSelectElement).value);
          this.offset = 0;
          void this.load();
        },
      },
      LIMIT_OPTIONS.map((n) =>
        el("option", { value: String(n), selected: n === this.limit }, [`${n} / page`])
      )
    );

    const prevButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        disabled: this.offset === 0,
        "aria-label": "Previous page",
        onclick: () => {
          this.offset = Math.max(0, this.offset - this.limit);
          void this.load();
        },
      },
      ["Prev"]
    );

    const nextButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        disabled: end >= this.total,
        "aria-label": "Next page",
        onclick: () => {
          this.offset = this.offset + this.limit;
          void this.load();
        },
      },
      ["Next"]
    );

    return el("div", { className: "pagination-bar" }, [
      el("span", { className: "next-cron__empty" }, [
        `Showing ${formatNumber(start)}-${formatNumber(end)} of ${formatNumber(this.total)}`,
      ]),
      el("div", { className: "row row--wrap" }, [limitSelect, prevButton, nextButton]),
    ]);
  }

  private openDetail(item: AccessLogItem): void {
    openModal(renderDetail(item), { title: `Access log #${item.id}` });
  }
}

function stopRow(ev: Event): void {
  ev.stopPropagation();
}

function headerSelect(
  ariaLabel: string,
  options: HTMLElement[],
  onChange: (value: string) => void
): HTMLSelectElement {
  const select = el(
    "select",
    {
      className: "input input--header",
      "aria-label": ariaLabel,
      onclick: stopRow,
      onchange: (ev: Event) => {
        const value = (ev.target as HTMLSelectElement).value;
        syncHeaderActive(select, value !== "");
        onChange(value);
      },
    },
    options
  ) as HTMLSelectElement;
  return select;
}

function syncHeaderActive(control: HTMLElement, active: boolean): void {
  control.classList.toggle("input--header-active", active);
}

function filterHeader(control: HTMLElement): HTMLElement {
  return el("th", { scope: "col", className: "data-table__th-filter" }, [control]);
}

function statusLabel(statusCode: number): HTMLElement {
  if (statusCode >= 400) {
    return renderStatusText(String(statusCode), "danger");
  }
  if (statusCode >= 300) {
    return renderStatusText(String(statusCode), "accent");
  }
  return renderStatusText(String(statusCode), "success");
}

function formatDurationMs(ms: number | null): string {
  if (ms === null || ms === undefined) return EMPTY;
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
}

function renderDetail(item: AccessLogItem): HTMLElement {
  const rows = [
    detailRow("Time", renderAbsoluteTime(item.requestedAt)),
    detailRow("Service", el("span", {}, [item.serviceAccountName || EMPTY])),
    detailRow("Service id", el("span", { className: "mono" }, [item.serviceAccountId != null ? String(item.serviceAccountId) : EMPTY])),
    detailRow("Data type", el("span", {}, [entityLabel(item.entity)])),
    detailRow("Method", el("span", { className: "mono" }, [item.method])),
    detailRow("Endpoint", el("span", { className: "mono" }, [item.endpoint])),
    detailRow("Status", statusLabel(item.statusCode)),
    detailRow("Duration", el("span", {}, [formatDurationMs(item.durationMs)])),
    detailRow("Records", el("span", { className: "tnum" }, [formatNumber(item.recordCount)])),
  ];

  const body: HTMLElement[] = [el("div", { className: "next-cron" }, rows)];

  if (item.queryParams && Object.keys(item.queryParams).length) {
    body.push(
      el("p", { className: "panel__title mt-8" }, ["Query parameters"]),
      el("pre", { className: "mono data-table__cell-wrap" }, [JSON.stringify(item.queryParams, null, 2)])
    );
  }

  return el("div", {}, body);
}

function detailRow(label: string, value: HTMLElement): HTMLElement {
  return el("div", { className: "next-cron__row" }, [
    el("span", { className: "next-cron__label" }, [label]),
    value,
  ]);
}

function toIsoStart(dateOnly: string): string {
  return dateOnly ? `${dateOnly}T00:00:00Z` : "";
}

function toIsoEnd(dateOnly: string): string {
  return dateOnly ? `${dateOnly}T23:59:59Z` : "";
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong loading this data.";
}
